import axios from "axios";
import { getAccessTokenBirthdate, peekAuthState } from "../../services/auth/auth-session.helpers";
import { ItineraryService } from "../../services/ItineraryService";
import { ephemeralStore, itineraryOfferRefreshSlice } from "../../store/ephemeral";
import { bikePrefsSlice, pricingPrefsSlice } from "../../store/persistence";
import { persistentSliceStore } from "../../store/persistence/runtime/PersistentSliceStore";
import { buildOfferPricingPreferences } from "../../tools/pricingPrefs.tools";
import type { ItineraryDTO } from "../../types/dtos/api/itinerary/itinerary.dto";

/** Backend error codes meaning the selected offers must be refreshed before prebooking. */
export const OFFER_REFRESH_ERROR_CODES: ReadonlySet<string> = new Set( [ "OFFER_EXPIRED", "OFFER_PRICE_CHANGED" ] );

const itineraryService = new ItineraryService();

/**
 * Reads the machine-readable error code of a failed purchase API call.
 *
 * NestJS error bodies carry the code in `message` (e.g. `OFFER_EXPIRED`,
 * `CFF_INVOICE_FAILED: {...upstream body}`); only the leading code is returned.
 *
 * @param error - Error thrown by an Axios call.
 * @returns HTTP status and error code (`null` when absent).
 */
export function readPurchaseError( error: unknown ): {
    "status": number | null;
    "code":   string | null;
} {
    if ( !axios.isAxiosError( error ) ) {
        return {
            "status": null,
            "code":   null,
        };
    }

    const data = error.response?.data as { "message"?: string | string[] } | undefined;
    const message = Array.isArray( data?.message ) ? data.message[0] : data?.message;

    return {
        "status": error.response?.status ?? null,
        "code":   typeof message === "string" ? message.split( ":" )[0].trim() : null,
    };
}

/**
 * Requests a fresh CFF offer for an itinerary and propagates it to the planner.
 *
 * The itinerary is sent **without** its `offer`: the backend otherwise reuses
 * the offer carried by the request for final segments, which would return the
 * expired offer again. The fresh itinerary is published on
 * `itineraryOfferRefreshSlice` so the itinerary list and the price pill pick it
 * up too. Correlation headers are added by the shared Axios interceptors.
 *
 * @param itinerary - Itinerary whose offer expired or changed.
 * @returns The itinerary with a freshly built offer.
 * @throws Error when `POST /api/sales/offer` fails.
 */
export async function refreshTicketPurchaseOffer( itinerary: ItineraryDTO ): Promise<ItineraryDTO> {
    const pricingPreferences = buildOfferPricingPreferences(
        persistentSliceStore.peek( pricingPrefsSlice ),
        persistentSliceStore.peek( bikePrefsSlice ),
        getAccessTokenBirthdate( peekAuthState().accessToken ?? "" ),
    );
    const withoutOffer: ItineraryDTO = { ...itinerary };
    delete withoutOffer.offer;

    const response = await itineraryService.evaluateItineraryOffer( withoutOffer, pricingPreferences );

    ephemeralStore.setValue( itineraryOfferRefreshSlice, ( previous ) => ( {
        "itinerary": response.itinerary,
        "revision":  previous.revision + 1,
    } ) );

    return response.itinerary;
}
